import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, LineChart, Line, Legend
} from 'recharts';
import {
  TrendingUp, Package, Truck, AlertTriangle, Scale, Activity,
  RefreshCw, Loader2, BarChart3, PieChart as PieChartIcon, 
  Route, Clock, DollarSign, FileText, Download
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useClientPermissions } from '../hooks/useClientPermissions';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import axios from 'axios';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#1db584', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#22c55e'];
const PERIOD_OPTIONS = [
  { value: '7d', label: '7 derniers jours' },
  { value: '30d', label: '30 derniers jours' },
  { value: '90d', label: '90 derniers jours' },
  { value: '12m', label: '12 derniers mois' },
  { value: 'ytd', label: 'Année en cours' },
  { value: 'all', label: 'Tout' },
];

const SummaryCard = ({ title, value, icon: Icon, color, sub }) => (
  <Card data-testid={`summary-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
        <div className={cn("p-3 rounded-xl", color)}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium text-gray-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          {p.name}: <strong>{typeof p.value === 'number' ? p.value.toLocaleString('fr-FR') : p.value}</strong>
        </p>
      ))}
    </div>
  );
};

const ClientAnalytics = () => {
  const { user } = useAuth();
  const permissions = useClientPermissions();
  const [period, setPeriod] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/client-analytics/overview`, {
        params: { period }
      });
      setData(response.data);
    } catch (err) {
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const formatCurrency = (v) => {
    if (v >= 1e9) return `${(v / 1e9).toFixed(1)} Mrd`;
    if (v >= 1e6) return `${(v / 1e6).toFixed(1)} M`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(0)} K`;
    return v.toLocaleString('fr-FR');
  };

  if (loading && !data) {
    return <LoadingSpinner />;
  }

  const d = data || {};
  const summary = d.summary || {};

  return (
    <div className="space-y-6" data-testid="client-analytics">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-xl md:text-3xl font-bold text-gray-900">Analytique</h1>
          <p className="text-gray-500 mt-1">Vue d'ensemble de votre activité transport</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[180px]" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchAnalytics} disabled={loading} data-testid="refresh-analytics">
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className={cn("grid gap-4", permissions.canViewFinances ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-6" : "grid-cols-2 md:grid-cols-3 lg:grid-cols-5")}>
        <SummaryCard title="Total" value={d.total || 0} icon={Package} color="bg-[#1db584]" />
        <SummaryCard title="En cours" value={(summary.preparation || 0) + (summary.execution || 0)} icon={Truck} color="bg-blue-500" sub={`${summary.preparation || 0} prep. / ${summary.execution || 0} exec.`} />
        <SummaryCard title="Terminées" value={summary.completed || 0} icon={Activity} color="bg-emerald-500" />
        <SummaryCard title="Annulées" value={summary.cancelled || 0} icon={AlertTriangle} color="bg-red-500" />
        <SummaryCard title="Volume" value={`${d.totalWeight || 0} T`} icon={Scale} color="bg-purple-500" />
        {permissions.canViewFinances && d.financial && (
          <SummaryCard title="Mes dépenses" value={`${formatCurrency(d.financial.totalEstimated)} XOF`} icon={DollarSign} color="bg-amber-500" sub={`Moy: ${formatCurrency(d.financial.avgPrice)} / op`} />
        )}
      </div>

      {d.total === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <BarChart3 className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-lg font-medium text-gray-500">Aucune donnée pour cette période</p>
            <p className="text-sm text-gray-400 mt-1">Modifiez la période ou créez des opérations</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Row 1: Activity Trend + Status Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2" data-testid="activity-trend-chart">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="w-4 h-4 text-[#1db584]" />
                  Activité
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={d.activityTrend || []}>
                      <defs>
                        <linearGradient id="colorActivity" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1db584" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#1db584" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="period" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                      <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="count" name="Opérations" stroke="#1db584" strokeWidth={2} fillOpacity={1} fill="url(#colorActivity)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="status-distribution-chart">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <PieChartIcon className="w-4 h-4 text-blue-500" />
                  Répartition
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={d.statusDistribution || []}
                        dataKey="value"
                        nameKey="name"
                        cx="50%" cy="50%"
                        innerRadius={50} outerRadius={80}
                        paddingAngle={2}
                      >
                        {(d.statusDistribution || []).map((_, idx) => (
                          <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-1.5 mt-2">
                  {(d.statusDistribution || []).slice(0, 5).map((item, idx) => (
                    <div key={item.key} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[idx % COLORS.length] }} />
                        <span className="text-gray-600">{item.name}</span>
                      </div>
                      <span className="font-semibold text-gray-900">{item.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Row 2: Top Routes + Delivery Time + Incidents */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2" data-testid="top-routes-chart">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Route className="w-4 h-4 text-amber-500" />
                  Top Routes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={(d.topRoutes || []).slice(0, 7)} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 12 }} stroke="#9ca3af" allowDecimals={false} />
                      <YAxis
                        type="category" dataKey="route" width={180}
                        tick={{ fontSize: 11 }} stroke="#9ca3af"
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" name="Opérations" fill="#f59e0b" radius={[0, 4, 4, 0]} barSize={24} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <Card data-testid="delivery-time-card">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2.5 rounded-xl bg-blue-50">
                      <Clock className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-medium">Délai moyen</p>
                      <p className="text-2xl font-bold text-gray-900">{d.avgDeliveryDays || 0} <span className="text-sm font-normal text-gray-500">jours</span></p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400">Temps moyen entre la création et la clôture d'une opération</p>
                </CardContent>
              </Card>

              <Card data-testid="incident-rate-card">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={cn("p-2.5 rounded-xl", d.incidentRate > 10 ? "bg-red-50" : "bg-emerald-50")}>
                      <AlertTriangle className={cn("w-5 h-5", d.incidentRate > 10 ? "text-red-600" : "text-emerald-600")} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-medium">Taux d'incidents</p>
                      <p className="text-2xl font-bold text-gray-900">{d.incidentRate || 0}<span className="text-sm font-normal text-gray-500">%</span></p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400">{d.incidentCount || 0} incident{(d.incidentCount || 0) > 1 ? 's' : ''} sur {d.total} opérations</p>
                </CardContent>
              </Card>

              {permissions.canViewFinances && d.financial && (
                <Card data-testid="financial-summary-card">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2.5 rounded-xl bg-amber-50">
                        <DollarSign className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-medium">Total réglé</p>
                        <p className="text-xl font-bold text-gray-900">{formatCurrency(d.financial.totalInvoiced)} <span className="text-xs font-normal text-gray-500">XOF</span></p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Total des prestations</span>
                      <span className="font-medium">{formatCurrency(d.financial.totalEstimated)} XOF</span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Row 3: Volume Trend */}
          {(d.volumeTrend || []).length > 0 && (
            <Card data-testid="volume-trend-chart">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Scale className="w-4 h-4 text-purple-500" />
                  Volume transporté (tonnes)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={d.volumeTrend}>
                      <defs>
                        <linearGradient id="colorVolume" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.3} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="period" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                      <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="volume" name="Volume (T)" fill="url(#colorVolume)" radius={[4, 4, 0, 0]} barSize={40} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default ClientAnalytics;
